/**
 * @file lib/git/chat-history-utils.ts
 * @description チャット履歴の復元・検索ヘルパー関数
 * @author GFTD Co., Ltd.
 */

import { ChatGitManager } from './chat-git-manager';
import { GitConfig } from './git-fs';
import {
  ChatGitConfig,
  ChatHistoryFile,
  ChatHistorySearchQuery,
  ChatHistorySearchResult
} from '../types/chat-git';
import { ActorSession, ActorMessage } from '../types/session';

/**
 * デフォルトのGit設定を作成
 */
export function createDefaultChatGitConfig(
  repoUrl: string,
  token: string,
  authorName: string = 'Chat History Manager',
  authorEmail: string = 'chat@gftd.ai'
): ChatGitConfig {
  return {
    git: {
      url: repoUrl,
      token,
      author: {
        name: authorName,
        email: authorEmail
      },
      branch: 'main',
      dir: '/chat-history'
    },
    format: 'both',
    autoCommit: true,
    commitMessageTemplate: 'チャット履歴更新: {sessionId} ({messageCount}件)',
    baseDirectory: 'sessions'
  };
}

/**
 * チャット履歴をクイック保存
 */
export async function saveSessionHistory(
  session: ActorSession,
  messages: ActorMessage[],
  gitConfig: ChatGitConfig,
  customCommitMessage?: string
): Promise<boolean> {
  try {
    const manager = new ChatGitManager(gitConfig);
    const result = await manager.saveHistory(session, messages, customCommitMessage);
    return result.success;
  } catch (error) {
    console.error('Failed to save session history:', error);
    return false;
  }
}

/**
 * チャット履歴を検索
 */
export async function searchChatHistory(
  query: ChatHistorySearchQuery,
  gitConfig: ChatGitConfig
): Promise<ChatHistorySearchResult[]> {
  try {
    const manager = new ChatGitManager(gitConfig);
    return await manager.searchHistory(query);
  } catch (error) {
    console.error('Failed to search chat history:', error);
    return [];
  }
}

/**
 * セッション履歴を復元
 */
export async function restoreSessionHistory(
  sessionId: string,
  gitConfig: ChatGitConfig,
  branch?: string
): Promise<ChatHistoryFile | null> {
  try {
    const manager = new ChatGitManager(gitConfig);
    return await manager.loadHistory(sessionId, branch);
  } catch (error) {
    console.error('Failed to restore session history:', error);
    return null;
  }
}

/**
 * アクターの全セッション履歴を検索
 */
export async function findActorSessions(
  actorId: string,
  gitConfig: ChatGitConfig,
  limit?: number
): Promise<ChatHistorySearchResult[]> {
  return await searchChatHistory(
    {
      actorId,
      limit
    },
    gitConfig
  );
}

/**
 * 期間で履歴を検索
 */
export async function findSessionsByDateRange(
  dateFrom: string,
  dateTo: string,
  gitConfig: ChatGitConfig,
  actorId?: string
): Promise<ChatHistorySearchResult[]> {
  return await searchChatHistory(
    {
      dateFrom,
      dateTo,
      actorId
    },
    gitConfig
  );
}

/**
 * キーワードで履歴を検索
 */
export async function findSessionsByKeyword(
  keyword: string,
  gitConfig: ChatGitConfig,
  actorId?: string,
  limit?: number
): Promise<ChatHistorySearchResult[]> {
  return await searchChatHistory(
    {
      keyword,
      actorId,
      limit
    },
    gitConfig
  );
}

/**
 * セッションの統計情報を取得
 */
export async function getSessionStatistics(
  sessionId: string,
  gitConfig: ChatGitConfig
): Promise<{
  messageCount: number;
  firstMessageDate: string;
  lastMessageDate: string;
  commitCount: number;
  fileSizes: { markdown?: number; json?: number };
} | null> {
  try {
    const manager = new ChatGitManager(gitConfig);
    const history = await manager.loadHistory(sessionId);
    
    if (!history) {
      return null;
    }

    const commits = await manager.getCommitHistory(sessionId, 100);
    
    return {
      messageCount: history.messages.length,
      firstMessageDate: history.messages[0]?.created_at || '',
      lastMessageDate: history.messages[history.messages.length - 1]?.created_at || '',
      commitCount: commits.length,
      fileSizes: {
        // ファイルサイズは実装による
        markdown: 0,
        json: 0
      }
    };
  } catch (error) {
    console.error('Failed to get session statistics:', error);
    return null;
  }
}

/**
 * 複数のセッション履歴をまとめて取得
 */
export async function bulkRestoreSessions(
  sessionIds: string[],
  gitConfig: ChatGitConfig
): Promise<Map<string, ChatHistoryFile | null>> {
  const results = new Map<string, ChatHistoryFile | null>();
  
  for (const sessionId of sessionIds) {
    try {
      const history = await restoreSessionHistory(sessionId, gitConfig);
      results.set(sessionId, history);
    } catch (error) {
      console.error(`Failed to restore session ${sessionId}:`, error);
      results.set(sessionId, null);
    }
  }
  
  return results;
}

/**
 * チャット履歴をMarkdown文字列としてエクスポート
 */
export function exportHistoryAsMarkdown(
  historyFile: ChatHistoryFile,
  options?: {
    includeMetadata?: boolean;
    includeToc?: boolean;
    timestampFormat?: string;
  }
): string {
  const { metadata, session, messages } = historyFile;
  const opts = {
    includeMetadata: true,
    includeToc: true,
    timestampFormat: 'YYYY-MM-DD HH:mm:ss',
    ...options
  };

  let markdown = '';

  // タイトル
  markdown += `# Chat History - Session ${session.id}\n\n`;

  // メタデータ
  if (opts.includeMetadata) {
    markdown += '## Session Information\n\n';
    markdown += `- **Session ID**: ${metadata.sessionId}\n`;
    markdown += `- **Actor ID**: ${metadata.actorId}\n`;
    markdown += `- **Created**: ${metadata.createdAt}\n`;
    markdown += `- **Updated**: ${metadata.updatedAt}\n`;
    markdown += `- **Total Messages**: ${metadata.messageCount}\n`;
    markdown += `- **Format**: ${metadata.format}\n`;
    if (metadata.commitSha) {
      markdown += `- **Commit SHA**: ${metadata.commitSha}\n`;
    }
    markdown += `- **Branch**: ${metadata.branch}\n\n`;
  }

  // 目次
  if (opts.includeToc && messages.length > 0) {
    markdown += '## Table of Contents\n\n';
    messages.forEach((msg, index) => {
      const timestamp = new Date(msg.created_at).toLocaleString();
      markdown += `${index + 1}. [${msg.role} (${timestamp})](#message-${index + 1})\n`;
    });
    markdown += '\n';
  }

  // メッセージ
  if (messages.length > 0) {
    markdown += '## Messages\n\n';
    messages.forEach((msg, index) => {
      const timestamp = new Date(msg.created_at).toLocaleString();
      const content = typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content, null, 2);
      
      markdown += `<a id="message-${index + 1}"></a>\n`;
      markdown += `### Message ${index + 1}\n\n`;
      markdown += `**Role**: ${msg.role}  \n`;
      markdown += `**Timestamp**: ${timestamp}  \n`;
      markdown += `**ID**: ${msg.id}\n\n`;
      
      if (typeof msg.content === 'string') {
        markdown += `${content}\n\n`;
      } else {
        markdown += '```json\n';
        markdown += `${content}\n`;
        markdown += '```\n\n';
      }
      
      if (msg.metadata) {
        markdown += '**Metadata**:\n';
        markdown += '```json\n';
        markdown += `${JSON.stringify(msg.metadata, null, 2)}\n`;
        markdown += '```\n\n';
      }
      
      markdown += '---\n\n';
    });
  } else {
    markdown += '## Messages\n\n';
    markdown += '*No messages in this session.*\n\n';
  }

  return markdown;
}

/**
 * 高度な検索クエリビルダー
 */
export class ChatHistoryQueryBuilder {
  private query: ChatHistorySearchQuery = {};

  sessionId(id: string): this {
    this.query.sessionId = id;
    return this;
  }

  actorId(id: string): this {
    this.query.actorId = id;
    return this;
  }

  keyword(keyword: string): this {
    this.query.keyword = keyword;
    return this;
  }

  dateRange(from: string, to: string): this {
    this.query.dateFrom = from;
    this.query.dateTo = to;
    return this;
  }

  branch(branch: string): this {
    this.query.branch = branch;
    return this;
  }

  limit(limit: number): this {
    this.query.limit = limit;
    return this;
  }

  build(): ChatHistorySearchQuery {
    return { ...this.query };
  }

  async execute(gitConfig: ChatGitConfig): Promise<ChatHistorySearchResult[]> {
    return await searchChatHistory(this.query, gitConfig);
  }
} 